import React from 'react';

export default function PatientResourcesPage() {
  const resources = [
    {
      title: "Test Preparation Guide",
      icon: "📋",
      description: "Learn how to properly prepare for your laboratory tests",
      items: [
        "Fasting requirements for blood work",
        "Medication guidelines before testing",
        "What to bring to your appointment",
        "Hydration recommendations"
      ]
    },
    {
      title: "Understanding Your Results",
      icon: "📊",
      description: "Comprehensive guide to interpreting laboratory test results",
      items: [
        "Normal reference ranges explained",
        "Common test abbreviations",
        "When to contact your physician",
        "Follow-up testing recommendations"
      ]
    },
    {
      title: "Insurance & Billing",
      icon: "💳",
      description: "Information about insurance coverage and payment options",
      items: [
        "Accepted insurance providers",
        "Self-pay pricing options",
        "Payment plans available",
        "How to submit claims"
      ]
    },
    {
      title: "Patient Portal Access",
      icon: "🔐",
      description: "Secure online access to your test results and medical records",
      items: [
        "24/7 result viewing",
        "Download and print reports",
        "Track testing history",
        "Request result copies"
      ]
    }
  ];

  const faqs = [
    {
      question: "How long does it take to get my test results?",
      answer: "Turnaround times vary by test type. Rapid tests provide results in 15-30 minutes, while most standard tests are completed within 24-48 hours. Complex tests may take 3-7 business days. You'll receive notification when results are ready."
    },
    {
      question: "Do I need an appointment?",
      answer: "While walk-ins are welcome, we recommend scheduling an appointment to minimize wait times. You can book online through our appointment page or call us at (984) 230-9599."
    },
    {
      question: "What forms of payment do you accept?",
      answer: "We accept most major insurance plans, Medicare, Medicaid, cash, credit cards (Visa, MasterCard, Discover, American Express), and debit cards. Payment plans are available for qualifying patients."
    },
    {
      question: "Can I access my results online?",
      answer: "Yes! All patients have access to our secure online portal where you can view, download, and print your test results 24/7. You'll receive login credentials after your first visit."
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-[#4AB8D8] hover:text-[#F8A356]">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Patient Resources</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Patient Resources</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Everything you need to know about our testing services, procedures, and patient care
          </p>
        </div>
      </div>

      {/* Resource Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {resources.map((resource, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md p-8 hover:shadow-xl transition-shadow">
              <div className="text-5xl mb-4">{resource.icon}</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{resource.title}</h2>
              <p className="text-gray-600 mb-4">{resource.description}</p>
              <ul className="space-y-2">
                {resource.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="flex items-start gap-2 text-gray-700">
                    <svg className="w-5 h-5 text-[#F8A356] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Forms and Documents */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Forms & Documents</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="#" className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#4AB8D8] hover:bg-[#4AB8D8]/10 transition-all group">
              <div className="text-[#4AB8D8] mb-3">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2 group-hover:text-[#4AB8D8]">New Patient Form</h3>
              <p className="text-gray-600 text-sm">Download and complete before your first visit</p>
            </a>
            <a href="#" className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#4AB8D8] hover:bg-[#4AB8D8]/10 transition-all group">
              <div className="text-[#4AB8D8] mb-3">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2 group-hover:text-[#4AB8D8]">Consent Forms</h3>
              <p className="text-gray-600 text-sm">Required authorization and consent documents</p>
            </a>
            <a href="#" className="border-2 border-gray-200 rounded-lg p-6 hover:border-[#4AB8D8] hover:bg-[#4AB8D8]/10 transition-all group">
              <div className="text-[#4AB8D8] mb-3">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2 group-hover:text-[#4AB8D8]">Insurance Information</h3>
              <p className="text-gray-600 text-sm">Insurance verification and billing forms</p>
            </a>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-gray-200 pb-6 last:border-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start gap-2">
                  <span className="text-[#F8A356]">Q:</span>
                  {faq.question}
                </h3>
                <p className="text-gray-700 pl-6">
                  <span className="text-[#4AB8D8] font-semibold">A:</span> {faq.answer}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a href="/faq" className="text-[#4AB8D8] hover:text-[#F8A356] font-semibold text-lg">
              View All FAQs →
            </a>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] rounded-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-xl mb-6 text-white/90">Our patient care team is here to help</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-[#4AB8D8] px-8 py-3 rounded-lg hover:bg-[#F8A356] hover:text-white transition-colors font-semibold">
              Contact Us
            </a>
            <a href="tel:9842309599" className="bg-[#3A9AB8] text-white px-8 py-3 rounded-lg hover:bg-[#F8A356] transition-colors font-semibold">
              Call (984) 230-9599
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
